/**
 * Translation API helper — EPIC-06-04 AC-3, AC-4.
 *
 * Calls POST /api/v1/translate.  Returns the translated string on success,
 * or null on any error / timeout so the caller can fall back to the original.
 *
 * AC-4: Never throws — all failures are caught and return null.
 */

/**
 * Translate `text` from `sourceLang` to `targetLang`.
 *
 * @param text          Text to translate (plain string, not logged).
 * @param sourceLang    BCP-47 source language code, e.g. "en".
 * @param targetLang    BCP-47 target language code, e.g. "zh-TW".
 * @param accessToken   Bearer JWT for the /api/v1/translate endpoint.
 * @param timeoutMs     Abort the request after this many milliseconds (AC-4).
 * @returns             Translated text, or null on failure / timeout.
 */
export async function translateText(
  text: string,
  sourceLang: string,
  targetLang: string,
  accessToken: string,
  timeoutMs: number,
): Promise<string | null> {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch('/api/v1/translate', {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        text,
        source_language: sourceLang,
        target_language: targetLang,
      }),
    });

    if (!response.ok) return null;

    const envelope = (await response.json()) as {
      status: string;
      data: { translated_text: string } | null;
      error: unknown;
    };

    return envelope.data?.translated_text ?? null;
  } catch {
    // Network error, AbortError (timeout), or JSON parse failure — AC-4 fallback
    return null;
  } finally {
    clearTimeout(timerId);
  }
}
